import { CameraController } from './controls/CameraController';
import { RENDERER_CONFIG } from '@/configs/rendererConfig';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { Loader } from './utils/Loader';
import { GUI } from 'dat.gui';
import { CreatorAction, UUID } from '@/types/types';
import { BoxMeasures } from './utils/BoxMeasures';
import { ActionHandler } from './utils/ActionHandler';
import { InputHandler } from './input/InputHandler';
import { SofaModule } from './objects/SofaModule';
import { ConnectingSlot } from './objects/ConnectingSlot';
import { EventEmitter } from './utils/EventEmitter';
import {
	SerializedRenderer,
	ThreeEvent,
	ThreeEvents,
	UserDataProp,
} from './types';
import { Postprocessing } from './Postprocessing';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import { Exporter, ExportType } from './Exporter';
import { ModulesManager } from './ModuleManager';
import { HistoryManager } from './HistoryManager';
import { CollectionBuilder } from './CollectionBuilder';
import { LightController } from './controls/LightsController';
import {
	WebGLRenderer,
	Group,
	PMREMGenerator,
	Scene,
	PCFShadowMap,
	SRGBColorSpace,
	Color,
} from 'three';

export type SyncAssetsPayload = Record<UUID, string>;

export class ProjectRenderer {
	private static instance: ProjectRenderer = new ProjectRenderer();
	private labelRenderer!: CSS2DRenderer;
	private inputHandler = new InputHandler(this);
	private loader = Loader.getInstance();
	private _selectedConnectingSlot: ConnectingSlot | null = null;
	private exporter = new Exporter(this);
	private boxMeasures = new BoxMeasures(this);
	private lightsController!: LightController;
	isMobile = false;
	screenshotRenderer!: WebGLRenderer;
	historyManger = new HistoryManager(this);
	actionHandler = new ActionHandler(this);
	moduleManager = new ModulesManager(this);
	collectionBuilder = new CollectionBuilder(this);
	postprocessing!: Postprocessing;
	renderer!: WebGLRenderer;
	emitter = new EventEmitter<ThreeEvents>();
	loading = false;
	params = {
		exposure: 1.0,
		toneMapping: 'Neutral',
		blurriness: 0.3,
		intensity: 1.0,
	};
	GUI!: GUI;
	collection = new Group();
	scene!: Scene;
	canvas: HTMLCanvasElement | null = null;
	cameraController!: CameraController;

	private constructor() {
		this.collection.userData = {
			[UserDataProp.IS_COLLECTION]: true,
		};
	}

	static getInstance() {
		return ProjectRenderer.instance;
	}

	get selectedConnectingSlot() {
		return this._selectedConnectingSlot;
	}

	set selectedConnectingSlot(slot: ConnectingSlot | null) {
		this.deselectAllModules();
		this._selectedConnectingSlot = slot;

		this.setSlotsVisibility();

		if (slot)
			this.emitter.emit(ThreeEvent.SLOT_SELECTION, {
				creatorProductModuleId: slot.hostModule.id,
				slotType: slot.slotType,
			});
	}

	getSelectedModule() {
		return Array.from(this.moduleManager.sofaModulesMap.values()).find(
			(mod) => mod.selected
		);
	}

	selectModule(sofaModule: SofaModule) {
		this.deselectAll();
		sofaModule.selected = true;

		this.emitter.emit(ThreeEvent.MODULE_SELECTION, {
			id: sofaModule.id,
			deleteable: sofaModule.isDeleteable(),
		});
	}

	private setSlotsVisibility() {
		this.moduleManager.sofaModulesMap.forEach((mod) => {
			for (const slot of mod.connectingSlots) {
				slot.setVisibility();
			}
		});
	}

	private initGUI() {}

	private initRenderers() {
		this.renderer = new WebGLRenderer({
			canvas: this.canvas!,
			antialias: true,
		});

		const offscreenCanvas = new OffscreenCanvas(
			window.innerWidth,
			window.innerHeight
		);

		this.screenshotRenderer = new WebGLRenderer({
			canvas: offscreenCanvas,
			antialias: true,
			preserveDrawingBuffer: true,
		});

		for (const renderer of [this.renderer, this.screenshotRenderer]) {
			renderer.shadowMap.enabled = true;
			renderer.shadowMap.type = PCFShadowMap;
			renderer.outputColorSpace = SRGBColorSpace;
		}

		this.labelRenderer = new CSS2DRenderer();

		this.labelRenderer.domElement.style.position = 'absolute';
		this.labelRenderer.domElement.style.zIndex = '300';
		this.labelRenderer.domElement.style.top = '0px';
		this.labelRenderer.domElement.style.pointerEvents = 'none';
		this.canvas!.parentElement!.appendChild(this.labelRenderer.domElement);

		this.renderer.setAnimationLoop(() => {
			this.cameraController.controls.update();

			this.renderer.render(this.scene, this.cameraController.camera);
			this.labelRenderer.render(this.scene, this.cameraController.camera);
			this.postprocessing.composer.render();
		});
	}

	deselectAll() {
		this.deselectAllModules();
		this.selectedConnectingSlot = null;

		this.emitter.emit(ThreeEvent.DESELECT_ALL, undefined);
	}

	private deselectAllModules() {
		this.moduleManager.sofaModulesMap.forEach((mod) => {
			mod.selected = false;
		});
	}

	toggleBoxMeasuresVisibility() {
		this.boxMeasures.toggleVisibility();
	}

	init(canvas: HTMLCanvasElement, isMobile: boolean) {
		this.canvas = canvas;
		this.isMobile = isMobile;

		this.initGUI();

		this.scene = new Scene();

		this.inputHandler.init();

		this.scene.background = new Color(RENDERER_CONFIG.SCENE_BACKGROUND);

		this.scene.add(this.collection);

		this.cameraController = new CameraController(this);
		this.lightsController = new LightController(this);

		this.initRenderers();
		this.loadEnvironmentMap();

		this.postprocessing = new Postprocessing(this);

		this.handleResize();
	}

	private loadEnvironmentMap() {
		const pmremGenerator = new PMREMGenerator(this.renderer);
		const envMap = pmremGenerator.fromScene(new RoomEnvironment()).texture;
		this.scene.environment = envMap;
		this.scene.environmentIntensity = 0.5;
		pmremGenerator.dispose();
	}

	handleActions(
		creatorActions: CreatorAction[],
		target?: UUID,
		skipHistory?: true
	) {
		if (!skipHistory) this.historyManger.addSnapshot();

		if (!target) {
			for (const action of creatorActions) {
				this.actionHandler.dispatchAction(action);
			}
			return;
		}

		const targetModule = this.moduleManager.sofaModulesMap.get(target);
		if (!targetModule) return;

		for (const action of creatorActions) {
			this.actionHandler.dispatchAction(action, targetModule);
		}
	}

	screenshot() {
		this.cameraController.prepareScreenshotCamera();
		this.screenshotRenderer.render(
			this.scene,
			this.cameraController.screenshotCamera
		);

		return (
			this.screenshotRenderer.domElement as unknown as OffscreenCanvas
		).convertToBlob();
	}

	modulesChangeSideEffects() {
		this.deselectAll();
		this.setSlotsVisibility();
		this.boxMeasures.draw();
	}

	async startCollection(creatorModule: CreatorProductModule) {
		this.moduleManager.clearCollection();
		await this.moduleManager.addSofaModule(creatorModule, true);
		this.modulesChangeSideEffects();
		this.cameraController.centerCamera();
	}

	removeSofaModule(creatorModule: CreatorProductModule) {
		this.historyManger.addSnapshot();
		this.moduleManager.removeSofaModule(creatorModule);
		this.modulesChangeSideEffects();
	}

	async addSofaModule(creatorModule: CreatorProductModule) {
		this.historyManger.addSnapshot();
		await this.moduleManager.addSofaModule(creatorModule);
		this.modulesChangeSideEffects();
	}

	handleCameraCenter() {
		this.cameraController.centerCamera();
	}

	handleCameraZoom(cameraDistance: number) {
		this.cameraController.setDistanceToTarget(cameraDistance);
	}

	handleResize() {
		const { width, height } =
			this.canvas!.parentElement!.getBoundingClientRect();

		this.cameraController.handleResize(width / height);

		this.renderer.setSize(width, height);
		this.labelRenderer.setSize(width, height);
		this.postprocessing.handleResize();
	}

	async saveModel() {
		const glb = await this.loader.gltfExporter.parseAsync(this.collection, {
			binary: true,
		});

		return glb as ArrayBuffer;
	}

	async undo() {
		await this.historyManger.undo();
		this.boxMeasures.draw();
	}

	handleExportCollection(type: ExportType) {
		this.exporter.exportCollection(type);
	}

	dispose() {
		this.renderer.dispose();
		this.cameraController.dispose();

		const ctx = this.canvas!.getContext('webgl2')!;
		ctx.clear(RENDERER_CONFIG.SCENE_BACKGROUND);
	}

	export(): SerializedRenderer {
		return {
			modulesManager: this.moduleManager.export(),
		};
	}
}
